// Settings screen - game configuration options

import { Canvas } from '../Canvas';
import { CONSTANTS, ScreenState } from '../types';
import { Screen, ScreenContext } from './Screen';

// Settings storage key
const SETTINGS_KEY = 'openarena_settings';

// Animation speed options
export enum AnimationSpeed {
  SLOW = 'slow',
  NORMAL = 'normal',
  FAST = 'fast',
}

// Animation speed multipliers (higher = faster)
export const ANIMATION_SPEED_MULTIPLIER: Record<AnimationSpeed, number> = {
  [AnimationSpeed.SLOW]: 0.5,
  [AnimationSpeed.NORMAL]: 1.0,
  [AnimationSpeed.FAST]: 2.0,
};

// Settings interface
export interface GameSettings {
  animationSpeed: AnimationSpeed;
  autoSave: boolean;
}

// Default settings
const DEFAULT_SETTINGS: GameSettings = {
  animationSpeed: AnimationSpeed.NORMAL,
  autoSave: true,
};

// Load settings from localStorage
export function loadSettings(): GameSettings {
  try {
    const json = localStorage.getItem(SETTINGS_KEY);
    if (json) {
      const saved = JSON.parse(json);
      return {
        ...DEFAULT_SETTINGS,
        ...saved,
      };
    }
  } catch (error) {
    console.error('Failed to load settings:', error);
  }
  return { ...DEFAULT_SETTINGS };
}

// Save settings to localStorage
export function saveSettings(settings: GameSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save settings:', error);
  }
}

// Get current animation speed multiplier
export function getAnimationSpeedMultiplier(): number {
  const settings = loadSettings();
  return ANIMATION_SPEED_MULTIPLIER[settings.animationSpeed];
}

export class Settings implements Screen {
  readonly state = ScreenState.SETTINGS;
  private context: ScreenContext;
  private settings: GameSettings;

  constructor(context: ScreenContext) {
    this.context = context;
    this.settings = loadSettings();
  }

  enter(): void {
    console.log('Entering Settings');
    this.settings = loadSettings();
  }

  exit(): void {
    console.log('Exiting Settings');
    saveSettings(this.settings);
  }

  update(_deltaTime: number): void {
    // No updates needed
  }

  render(canvas: Canvas): void {
    // Background
    canvas.drawRect(0, 0, CONSTANTS.CANVAS_WIDTH, CONSTANTS.CANVAS_HEIGHT, CONSTANTS.MAIN_BACKGROUND);

    // Title
    canvas.drawText('Asetukset', CONSTANTS.CANVAS_WIDTH / 2, 50, 'bold 32px "comic sans ms"', '#000080', 'center');

    // Settings panel
    const panelX = 200;
    const panelY = 120;
    const panelWidth = 624;
    const panelHeight = 400;

    canvas.drawRect(panelX, panelY, panelWidth, panelHeight, '#e0e0e0');
    canvas.drawRectOutline(panelX, panelY, panelWidth, panelHeight, '#000000');

    // Animation Speed setting
    this.renderAnimationSpeedSetting(canvas, panelX + 30, panelY + 30);

    // Auto-save setting
    this.renderAutoSaveSetting(canvas, panelX + 30, panelY + 150);

    // Back button
    canvas.drawButton(
      'back',
      'Takaisin',
      CONSTANTS.CANVAS_WIDTH / 2 - 60,
      panelY + panelHeight + 30,
      120,
      35,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      '#006600',
      () => this.context.onScreenChange(ScreenState.MAIN_MENU)
    );
  }

  private renderAnimationSpeedSetting(canvas: Canvas, x: number, y: number): void {
    canvas.drawText('Animaationopeus:', x, y, CONSTANTS.BIG_FONT, '#000000');

    const speedOptions: { speed: AnimationSpeed; label: string }[] = [
      { speed: AnimationSpeed.SLOW, label: 'Hidas' },
      { speed: AnimationSpeed.NORMAL, label: 'Normaali' },
      { speed: AnimationSpeed.FAST, label: 'Nopea' },
    ];

    const buttonWidth = 100;
    const buttonHeight = 30;
    const buttonSpacing = 20;
    const startX = x;
    const buttonY = y + 35;

    for (let i = 0; i < speedOptions.length; i++) {
      const option = speedOptions[i];
      if (!option) continue;

      const buttonX = startX + i * (buttonWidth + buttonSpacing);
      const isSelected = this.settings.animationSpeed === option.speed;
      const bgColor = isSelected ? '#006600' : '#666666';

      canvas.drawButton(
        `speed-${option.speed}`,
        option.label,
        buttonX,
        buttonY,
        buttonWidth,
        buttonHeight,
        CONSTANTS.MAIN_BUTTON_FONT,
        '#ffffff',
        bgColor,
        () => {
          this.settings.animationSpeed = option.speed;
          saveSettings(this.settings);
        }
      );
    }

    // Description
    const descriptions: Record<AnimationSpeed, string> = {
      [AnimationSpeed.SLOW]: 'Kaikki animaatiot näytetään hitaasti.',
      [AnimationSpeed.NORMAL]: 'Oletusnopeus.',
      [AnimationSpeed.FAST]: 'Taistelut etenevät nopeammin.',
    };

    canvas.drawText(
      descriptions[this.settings.animationSpeed],
      x,
      buttonY + buttonHeight + 15,
      CONSTANTS.MSG_FONT,
      '#444444'
    );
  }

  private renderAutoSaveSetting(canvas: Canvas, x: number, y: number): void {
    canvas.drawText('Automaattinen tallennus:', x, y, CONSTANTS.BIG_FONT, '#000000');

    const buttonY = y + 35;
    const buttonWidth = 80;
    const buttonHeight = 30;

    // On button
    const onSelected = this.settings.autoSave;
    canvas.drawButton(
      'autosave-on',
      'Päällä',
      x,
      buttonY,
      buttonWidth,
      buttonHeight,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      onSelected ? '#006600' : '#666666',
      () => {
        this.settings.autoSave = true;
        saveSettings(this.settings);
      }
    );

    // Off button
    canvas.drawButton(
      'autosave-off',
      'Pois',
      x + buttonWidth + 20,
      buttonY,
      buttonWidth,
      buttonHeight,
      CONSTANTS.MAIN_BUTTON_FONT,
      '#ffffff',
      !onSelected ? '#006600' : '#666666',
      () => {
        this.settings.autoSave = false;
        saveSettings(this.settings);
      }
    );

    // Description
    const description = this.settings.autoSave
      ? 'Peli tallennetaan automaattisesti kuukauden lopussa.'
      : 'Peli ei tallennu automaattisesti.';

    canvas.drawText(description, x, buttonY + buttonHeight + 15, CONSTANTS.MSG_FONT, '#444444');
  }

  handleClick(_x: number, _y: number): void {
    // Clicks handled by canvas click regions
  }

  handleKeyDown(key: string): void {
    if (key === 'Escape') {
      this.context.onScreenChange(ScreenState.MAIN_MENU);
    }
  }
}
